import { GeolocationSiteType } from '.';

/**
 * Site-type weight groups (PLT-7035) — how much a site counts in a climate
 * roll-up when no one has entered a weight for it.
 *
 * The groups follow how much of the business a physical event at the site can
 * stop: Heavy sites are where the plant, equipment or land is; Medium sites hold
 * buildings and stock that are costly but replaceable; Light sites are people
 * and desks, whose work can move. The 6 : 3 : 1 ratio comes from an asset-value
 * comparison of the three groups and is expected to be revisited — this table is
 * the one place to change it, so every roll-up that opts in to the type split
 * (`resolveEffectiveSiteWeights(sites, 'site-type')`) moves together.
 */
export const SITE_TYPE_WEIGHT_GROUPS = {
  Heavy: 6,
  Medium: 3,
  Light: 1,
} as const;
export type SiteTypeWeightGroup = keyof typeof SITE_TYPE_WEIGHT_GROUPS;

const GROUP_BY_SITE_TYPE: Record<GeolocationSiteType, SiteTypeWeightGroup> = {
  Production: 'Heavy',
  Extraction: 'Heavy',
  Biological: 'Heavy',
  'Utility infrastructure': 'Heavy',
  'Waste & recovery sites': 'Heavy',
  'Data centers': 'Heavy',
  'Logistics/infrastructure': 'Medium',
  'Construction sites': 'Medium',
  'Global HQ': 'Light',
  HQ: 'Light',
  'Corporate/administrative': 'Light',
  'Retail/customer-facing': 'Light',
};

const GROUP_BY_NORMALISED_SITE_TYPE = new Map<string, SiteTypeWeightGroup>(
  Object.entries(GROUP_BY_SITE_TYPE).map(([siteType, group]) => [normaliseSiteType(siteType), group]),
);

/** `locationType` is a free string; compare site types through this so casing and padding never matter. */
export function normaliseSiteType(siteType: string): string {
  return siteType.trim().toLowerCase();
}

/**
 * The weight group of a stored `locationType`.
 *
 * `locationType` is a free string, so anything outside the canonical taxonomy —
 * a missing type, or one the site finder has since renamed — lands in Medium.
 * Medium is the middle of the range, and unknown types are too rare to tell
 * which way they would bias a company's number.
 */
export function siteTypeWeightGroup(locationType: string | null | undefined): SiteTypeWeightGroup {
  if (!locationType) {
    return 'Medium';
  }
  return GROUP_BY_NORMALISED_SITE_TYPE.get(normaliseSiteType(locationType)) ?? 'Medium';
}

/** The relative weight of a stored `locationType` — see `SITE_TYPE_WEIGHT_GROUPS`. */
export function siteTypeWeight(locationType: string | null | undefined): number {
  return SITE_TYPE_WEIGHT_GROUPS[siteTypeWeightGroup(locationType)];
}
